import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Moment from 'moment';
import {
    get as _get,
    isEqual as _isEqual,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import {
    DataGridPro,
    useGridApiRef,
} from '@mui/x-data-grid-pro';

import CloseIcon from '@mui/icons-material/Close';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ReplayIcon from '@mui/icons-material/Replay';
import MenuIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QueueIcon from '@mui/icons-material/Queue';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import CancelledIcon from '@mui/icons-material/DoNotDisturb';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import Tooltip from '../../../components/styled/tooltip';
import Searchbar from '../../../components/searchbar';
import ProgDonutChart from '../../../components/charts/progdonutchart';
import ConfirmationDialog from '../../../components/confirmationdlg';
import SensorName from '../../../components/sensorname';
import ProgressBar from '../../../components/progress-bar';

import BacktestingService, { fields } from '../../../data/backtesting-service';
import SensorsService from '../../../data/sensors-service';
import CopyText from '../../../components/copytext';
import Auth, { ProfileContext, ParamsSerializer } from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

import useWindowSize from '../../../libraries/usewindowsize';
import helpers from '../../../libraries/helpers';
import { toast } from 'react-toastify';

import { LoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';

const useStyles = makeStyles(theme => ({
    flexFix: {
        display: 'flex',
    },
    fauxTableHeader: {
        fontSize: '25px',
        color: theme.palette.primary.main,
        borderBottom: `1px solid ${theme.palette.background.paper}`,
        padding: theme.spacing(2),
    },
    fauxTableRow: {
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.background.paper}`,
        '&:hover': {
            background: theme.palette.background.paper,
        },
    },
    visualsAccordion: {
        background: 'transparent',
        boxShadow: '0 0 0 0 transparent',
    },
    visualMinWidth: {
        minWidth: '260px',
    },
    btLegend: {
        display: 'flex',
        alignItems: 'flex-end',
        lineHeight: '18px',
        '&>div': {
            display: 'inline-block',
            height: 20,
            width: 20,
            border: `3px solid ${theme.palette.primary.main}`,
            borderRadius: 20,
            marginTop: 12,
            marginRight: 10,
            '&.complete': { borderColor: '#7BA9F2' },
            '&.queued': { borderColor: '#BFD855' },
            '&.created': { borderColor: '#1A9EA3' },
            '&.error': { borderColor: theme.palette.error.main },
            '&.cancelled': { borderColor: theme.palette.warning.main },
        },
    },
    status: {
        display: 'flex',
        alignItems: 'center',
    },
    successText: {
        color: theme.palette.success.main,
    },
    errorText: {
        color: theme.palette.error.main,
    },
    warningText: {
        color: theme.palette.warning.main,
    },
}));

const BAD_FILE_LOCATON = '/var/bricata/log/btd/pcap-skips.log';

const Backtesting = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { refreshBacktestingJobs } = useContext(DataContext);
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const { height } = useWindowSize();
    const tableAPIRef = useGridApiRef();
    const [columns, setColumns] = useState([]);
    const [columnWidth, setColumnWidth] = useState({});
    const [sortModel, setSortModel] = useState([{
        field: 'name',
        sort: 'asc',
    }]);
    const [sensors, setSensors] = useState({});
    const [search, setSearch] = useState(_get(state, 'job', ''));
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [fieldsFilter, setFieldsFilter] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [anchor, setAnchor] = useState(null);
    const [deleteJobDialog, setDeleteJobDialog] = useState(false);
    const [cancelAllJobsDialog, setCancelAllJobsDialog] = useState(false);
    const [donutData, setDonutData] = useState({});
    const [progData, setProgData] = useState({});
    const [visualsOpen, setVisualsOpen] = useState(true);
    const [tableHeight, setTableHeight] = useState(200);
    const [loading, setLoading] = useState(false);
    const btPerms = Auth.permissions.get('Backtesting');

    useEffect(() => {
        setColumnWidth(getProfile('backtesting').widths || {});
    }, [state, getProfile]);

    const sortSensors = (data) => {
        if (data.length) {
            const d = data.reduce((a, c) => {
                if (!Object.prototype.hasOwnProperty.call(a, c.sensor)) {
                    a[c.sensor] = [];
                }
                a[c.sensor].push(c);

                return a;
            }, {});
            setSensors(d);
        } else {
            SensorsService.getData().then(res => {
                const d = res.filter(v => {
                    return v.data.backtesting;
                }).reduce((a, c) => {
                    a[c.uuid] = [];
                    return a;
                }, {});
                setSensors(d);
            }, err => {
                console.error(err);
            });
        }
    };

    const handleUpdateSort = s => {
        if (!_isEqual(s, sortModel)) {
            setSortModel(s);
        }
    };

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('backtesting', {
            ...getProfile('backtesting'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const handleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = tableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('backtesting', {
                ...getProfile('backtesting'),
                columns: order,
            });
        });
    };

    const handleSearch = useCallback(({ search }) => {
        setTimeout(() => {
            if ((search || '').length < 1) {
                setFilteredJobs(jobs);
                return;
            }
            // setJobs(RESULTS.filter(v => v.name.includes(search)));
            setFilteredJobs(jobs.filter(v => {
                // exact match on uuid
                if (v.uuid === search) return true;

                const s = search.toLowerCase();
                const f = ['name', 'owner', 'data.activity.last_action', 'data.activity.actor', 'data.activity.timestamp', 'sensor', 'status', 'description'];
                const t = f.filter(x => {
                    return _get(v, x, '').toLowerCase().includes(s);
                });

                if (t.length || Moment(v.data.activity.timestamp).format(theme.date.short).includes(s)) {
                    return v;
                }

                return null;
            }));
        });
    }, [jobs, theme.date.short]);

    useEffect(() => {
        handleSearch({ search });
    }, [search, handleSearch]);

    const cancelBacktesting = useRef(null);
    const fetchJobs = useCallback(async (update) => {
        const fetch = () => {
            // this guy is getting called twice, once from app and once here,
            // probably revisit at a later date \_(ツ)_/¯
            setLoading(true);
            BacktestingService.getJobs().then(res => {
                sortSensors(Object.values(res || []));
                setJobs((res || []).map(v => ({
                    ...v,
                    id: helpers.UniqueID(),
                    // too expensive to figure out if we have these
                    results: ['Alerts', 'Metadata'],
                })));

                // tee up the next refresh
                const rate = (res || []).some(b =>
                    b.status === 'pushed' ||
                    b.status === 'running' ||
                    b.status === 'queued') ? 11000 : 42000;
                clearTimeout(cancelBacktesting.current);
                cancelBacktesting.current = setTimeout(fetch, rate);

                // update Context
                if (update) refreshBacktestingJobs();
            }).finally(() => setLoading(false));
        };

        clearTimeout(cancelBacktesting.current);
        fetch();
    }, [refreshBacktestingJobs]);

    useEffect(() => {
        // start the job refresh loop
        fetchJobs();

        return () => {
            cancelBacktesting && clearTimeout(cancelBacktesting.current);
        };
    }, [fetchJobs]);

    const handleSelectJob = (d) => {
        setSelectedJob(d);
    };

    const handleCreateJob = () => {
        navigate('/views/backtesting/create');
    };

    const handleOpenFilter = () => {
        setFilterOpen(!filterOpen);
    };

    const handleUpdateFieldsFilter = (e, d) => {
        setFieldsFilter(d);
    };

    const handlePlayJob = useCallback((e, name) => {
        e.stopPropagation();
        BacktestingService.pushJob(name).then(res => {
            fetchJobs(true);
            toast.success('Job pushed successfully');
        }, err => {
            toast.error('Unable to start job...');
            console.error('Unable to start job ::: ', err);
        });
    }, [fetchJobs]);

    const handleStopJob = useCallback((e, name) => {
        e.stopPropagation();
        BacktestingService.cancelJob(name).then(res => {
            fetchJobs(true);
            toast.success('Job stopped successfully');
        }, err => {
            console.log(err);
            toast.error('Job was not pushed');
        });
    }, [fetchJobs]);

    const handleEditJob = useCallback((e) => {
        e.stopPropagation();
        handleMenuClose();
        navigate('/views/backtesting/edit/' + selectedJob.row.name);
    }, [selectedJob, navigate]);

    const handleGoToData = useCallback((e, job, page) => {
        e.stopPropagation();
        if (!page || !job) return;

        const p = {
            // sort: 'timestamp desc',
            quick: 'job',
            backtesting: 1,
            selectedBTJobs: [job],
        };
        navigate(`/views/${page.toLowerCase()}?${ParamsSerializer(p)}`);
    }, [navigate]);

    const handleOpenDeleteJobDialog = () => {
        setDeleteJobDialog(true);
        handleMenuClose();
    };

    const handleCloseDeleteJobDialog = () => {
        setDeleteJobDialog(false);
        setSelectedJob(null);
    };

    const handleDeleteJob = (e) => {
        BacktestingService.deleteJob(selectedJob.row.name).then(() => {
            fetchJobs(true);
            toast.success('Job deleted successfully');
        }, err => {
            toast.error('Unable to delete job');
            console.error('Unable to delete job ::: ', err);
        });
        handleCloseDeleteJobDialog();
    };

    const handleMenuOpen = (e) => {
        setAnchor(e.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchor(null);
    };

    const handleToggleAllJobsDialog = () => {
        setCancelAllJobsDialog(!cancelAllJobsDialog);
    };

    const handleCancelAllJobs = () => {
        BacktestingService.cancelAllJobs().then(res => {
            toast.success('All jobs cancelled successfully');
            fetchJobs(true);
        }, err => {
            toast.error('Unable to cancel all jobs...');
            console.error('Unable to cancel all jobs ::: ', err);
        });
        handleToggleAllJobsDialog();
    };

    /* eslint-disable react/display-name */
    const tableRenderers = useMemo(() => ({
        name: d => (<Tooltip title={d.row.uuid || ''}><div>{d.row.name}</div></Tooltip>),
        username: d => (<div>{d.row.owner}</div>),
        activity: d => (<div>
            <Typography>{d.row.data.activity.last_action} - {d.row.data.activity.actor}</Typography>
            <Typography>{Moment(d.row.data.activity.timestamp).format(theme.date.short)}</Typography>
        </div>),
        sensor: d => {
            return <SensorName uuid={d.value || d} />;
        },
        status: d => {
            switch (d.row.status) {
                case 'queued-alt':
                    return (<div className={classes.status}>Queued
                        <Badge sx={{
                            '& .MuiBadge-badge': {
                                color: 'black',
                                backgroundColor: '#BFD855'
                            }
                        }} badgeContent={`${d.row.qorder + 1}`}>
                            <QueueIcon />
                        </Badge>
                    </div>);
                case 'queued':
                    return (<div className={classes.status}>Queued
                        <Chip style={{ marginLeft: 5, backgroundColor: '#BFD855', color: 'black' }} size="small" label={`${d.row.qorder + 1}`}/>
                    </div>);
                case 'created':
                    return (<div className={classes.status}>Created</div>);
                case 'complete':
                    return (<div className={classes.status}>Complete <CheckIcon className={classes.successText} /></div>);
                case 'error':
                    return (
                        <React.Fragment>
                            <CopyText
                                className={classes.copyText}
                                text="Error"
                                copy={BAD_FILE_LOCATON} />
                            <Tooltip title={d.row.data.error || 'Job has an error'}>
                                <div className={classes.status}>
                                    <ErrorIcon className={classes.errorText} />
                                </div>
                            </Tooltip>
                        </React.Fragment>
                    );
                case 'pushed':
                    return (<div className={classes.status}>Pushed</div>);
                case 'canceled':
                    return (<div className={classes.status}>Cancelled <CancelledIcon className={classes.warningText} /></div>);
                case 'running':
                    return (<ProgressBar color={'blue'} value={d.row.data.progress}/>);
            }
        },
        error: d => d.row.data.error
            ? <div className={classes.status}>
                <Tooltip title={d.row.data.error || 'Job has an error'}>
                    <ErrorIcon className={classes.errorText} />
                </Tooltip>
                <CopyText
                    className={classes.copyText}
                    text="Error"
                    copy={BAD_FILE_LOCATON} />
            </div>
            : '',
        description: d => (<div><Tooltip title={d.row.description}><span>{d.row.description}</span></Tooltip></div>),
        results: d => (['running', 'complete', 'canceled', 'error'].some(s => d.row.status === s) &&
            <div>{d.row.results.map((v, i) => (
                <span key={`result-${v}-${i}`}
                    style={{ padding: 5, cursor: 'pointer', textTransform: 'capitalize' }}>
                    <Link
                        onClick={e => handleGoToData(e, d.row.uuid, v)}
                        styleeeee={{ padding: 5, cursor: 'pointer', textTransform: 'capitalize' }}
                        key={`result-${v}-${i}`}>{v}
                    </Link>
                </span>
            ))}</div>),
        actions: (d) => {
            return (
                <Box display="flex" justifyContent="space-between">
                    <Box display={
                        d.row.status === 'queued' ||
                        d.row.status === 'created' ||
                        d.row.status === 'error' ||
                        d.row.status === 'pushed' ||
                        d.row.status === 'canceled' ? 'flex' : 'none'
                    }>
                        <IconButton
                            onClick={e => handlePlayJob(e, d.row.name)}
                            disabled={d.row.status === 'pushed' || !btPerms.admin}
                        >
                            <PlayIcon />
                        </IconButton>
                    </Box>
                    <Box display={d.row.status === 'running' ? 'flex' : 'none'}>
                        <IconButton disabled={!btPerms.admin} onClick={e => handleStopJob(e, d.row.name)}>
                            <StopIcon />
                        </IconButton>
                    </Box>
                    <Box display={d.row.status === 'complete' ? 'flex' : 'none'}>
                        <IconButton disabled={!btPerms.admin} onClick={e => handlePlayJob(e, d.row.name)}>
                            <ReplayIcon />
                        </IconButton>
                    </Box>
                    <Box>
                        <IconButton disabled={!btPerms.write || !btPerms.delete} onClick={e => handleMenuOpen(e)}>
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Box>
            );
        },
    }), [classes, handleGoToData, handlePlayJob, handleStopJob, theme]);

    const getProgDonutData = useCallback(() => {
        const dData = {};
        const pData = {};

        Object.keys(sensors).forEach((v) => {
            dData[v] = sensors[v].reduce((a, c) => {
                switch (c.status) {
                    case 'created':
                    case 'running':
                        a[0].count++;
                        break;
                    case 'queued':
                        a[1].count++;
                        break;
                    case 'complete':
                        a[2].count++;
                        break;
                    case 'error':
                        a[3].count++;
                        break;
                    case 'canceled':
                        a[4].count++;
                        break;
                }
                return a;
            }, [
                { count: 0, key: 'Created' },
                { count: 0, key: 'Queued' },
                { count: 0, key: 'Complete' },
                { count: 0, key: 'Error' },
                { count: 0, key: 'Cancelled' },
            ]);

            pData[v] = sensors[v].reduce((a, c) => {
                if (c.status === 'running') {
                    a[0].count = c.data.progress;
                    a[1].count = 100 - c.data.progress;
                }

                return a;
            }, [
                { count: 0, key: 'in' },
                { count: 100, key: 'out' }
            ]);
        });

        setDonutData({ ...dData });
        setProgData({ ...pData });
    }, [sensors]);

    useEffect(() => {
        getProgDonutData();
    }, [getProgDonutData]);

    const generateColumns = useCallback(() => {
        const p = getProfile('backtesting');
        let c = fields.map(f => ({
            ...f,
            width: columnWidth[f.headerName] || 0,
            ...(!columnWidth[f.headerName] ? { flex: 1 } : {}),
            disableColumnMenu: true,
            renderCell: tableRenderers[f.variable],
        }));

        if (p.columns && p.columns.length) {
            c = p.columns.map(v => {
                for (let i = c.length; i--;) {
                    if (v === c[i].headerName) {
                        return c[i];
                    }
                }
                return null;
            });
        }
        setColumns(c);
    }, [columnWidth, getProfile, tableRenderers]);

    useEffect(() => {
        generateColumns();
    }, [generateColumns]);

    // this will get kicked when sensors are updated
    useEffect(() => {
        getProgDonutData();
    }, [getProgDonutData]);

    const handleToggleVisuals = () => {
        setVisualsOpen(!visualsOpen);
    };

    useEffect(() => {
        setTableHeight(Math.max(height - (visualsOpen ? 580 : 280), 200));
    }, [height, visualsOpen]);

    return (
        <React.Fragment>
            <Grid container>
                <Grid item xs={12}>
                    <Box mb={2} display="flex" justifyContent="flex-end">
                        <Box mr={2}><Button onClick={handleToggleVisuals}>{visualsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} BT Sensors</Button></Box>
                        <Box mr={2}><Button disabled={!btPerms.admin} variant="contained" color="secondary" onClick={handleToggleAllJobsDialog}>Cancel all Jobs</Button></Box>
                        <Box><Button disabled={!btPerms.write} variant="contained" color="primary" onClick={handleCreateJob}>Create New Job</Button></Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Accordion className={classes.visualsAccordion} expanded={visualsOpen}>
                        <AccordionSummary style={{ display: 'none' }}></AccordionSummary>
                        <AccordionDetails>
                            <Box width="100%">
                                <Grid container>
                                    <Grid container item xs={12}>
                                        {Object.keys(sensors).map((v, i) => {
                                            return (
                                                <Grid item xs={3} key={`prog-donut-${i}`} className={classes.visualMinWidth}>
                                                    {donutData[v] && progData[v] && <ProgDonutChart
                                                        size={250}
                                                        data={(() => {
                                                            return _get(donutData, v, []);
                                                        })()}
                                                        progdata={_get(progData, v, [])}
                                                    />}
                                                    <Box mt={1} width="250px" textAlign="center">
                                                        <Typography><SensorName uuid={v} /></Typography>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                        <Grid item xs={3}>
                                            <Box mt={3}>
                                                <Box className={classes.btLegend}><div className="complete"></div>Complete</Box>
                                                <Box className={classes.btLegend}><div className="queued"></div>Queued</Box>
                                                <Box className={classes.btLegend}><div className="created"></div>Created</Box>
                                                <Box className={classes.btLegend}><div></div>Progress</Box>
                                                <Box className={classes.btLegend}><div className="error"></div>Error</Box>
                                                <Box className={classes.btLegend}><div className="cancelled"></div>Cancelled</Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                <Grid item xs={12}>
                    <Box mb={2} display="flex" justifyContent="flex-end">
                        {/* <Box mr={2}>
                            <Button color="inherit" onClick={handleOpenFilter}>
                                <FilterIcon />
                                <Box ml={1}>Filter</Box>
                            </Button>
                        </Box> */}
                        <Searchbar immediate onSearch={({ search }) => setSearch(search)} searchservice={BacktestingService} value={search}/>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box mb={filterOpen ? 2 : 0}>
                        <Accordion expanded={filterOpen}>
                            <AccordionDetails style={{ display: 'none' }}></AccordionDetails>
                            <AccordionSummary>
                                <Box width="100%">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Box display="flex" justifyContent="space-between">
                                                <Box mt={0.5}><Typography variant="h6">Filter</Typography></Box>
                                                <IconButton onClick={handleOpenFilter}><CloseIcon /></IconButton>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Autocomplete
                                                fullWidth
                                                multiple
                                                options={fields.map((v, i) => {
                                                    if (v.variable !== 'name' && v.variable !== 'actions') {
                                                        return v;
                                                    }
                                                    return null;
                                                }).filter(v => v)}
                                                getOptionLabel={v => v.headerName}
                                                renderInput={p => (
                                                    <TextField
                                                        {...p}
                                                        variant="outlined"
                                                        label="Fields"
                                                    />
                                                )}
                                                onChange={handleUpdateFieldsFilter}
                                                value={fieldsFilter}
                                            >
                                            </Autocomplete>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <TextField
                                                label="Terms"
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </AccordionSummary>
                        </Accordion>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box height={tableHeight} mb={'-45px'}>
                        <DataGridPro
                            components={{
                                LoadingOverlay: LoadingOverlay,
                                NoRowsOverlay: NoRowsOverlay,
                            }}
                            loading={loading === true}
                            apiRef={tableAPIRef}
                            rows={search || filteredJobs.length ? filteredJobs : jobs}
                            columns={columns}
                            onRowClick={handleSelectJob}
                            disableMultipleSelection
                            disableColumnMenu
                            disableColumnFilter
                            sortModel={sortModel}
                            onSortModelChange={handleUpdateSort}
                            onColumnResize={handleColumnResize}
                            onColumnOrderChange={handleColumnReorder}
                        />
                    </Box>
                    <Menu
                        open={Boolean(anchor)}
                        anchorEl={anchor}
                        onClose={handleMenuClose}
                    >
                        <MenuItem disabled={!btPerms.write} onClick={handleEditJob}><EditIcon /> Edit</MenuItem>
                        <MenuItem disabled={!btPerms.delete} onClick={handleOpenDeleteJobDialog}><DeleteIcon /> Delete</MenuItem>
                    </Menu>
                </Grid>
            </Grid>
            <Box mt={10} mb={10}></Box>
            <ConfirmationDialog
                onClose={handleCloseDeleteJobDialog}
                onOK={handleDeleteJob}
                open={deleteJobDialog}
                message={
                    `Are you sure you want to remove the job
                        ${selectedJob
            ? (selectedJob.row.name ? selectedJob.row.name : selectedJob.row.uuid)
            : ''}?`
                }
                title="Delete Job"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleAllJobsDialog}
                onOK={handleCancelAllJobs}
                open={cancelAllJobsDialog}
                message="Are you sure you want to cancel all jobs?"
                title="Cancel All Jobs"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </React.Fragment>
    );
};

export default Backtesting;
